import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useContext, useEffect } from 'react'
import { useLocation, useParams } from 'react-router'
import { isAxiosError } from 'axios'
import queryString from 'query-string'

import { MODALS_ID, QUERY_KEY } from '../../constants'
import type { operations } from '../../constants/schema'

import { updateToken } from '../../utils'
import { abrUserSignin, abrViewTerms } from '../../utils/abr'
import { queryClient } from '../../utils/queryClient'

import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useUserInfo } from '../../hooks/useUserInfo'

import { oAuthVerify } from '../../api/auth'

import AlreadyWithdrawalNotifyModal from './modals/AlreadyWithdrawalNotifyModal'

type Provider = operations['AuthController_webUrl']['parameters']['query']['provider']

const providers = ['yes24', 'kakao', 'google', 'naver', 'apple']

export function SignInVerify() {
  const { openModal } = useContext(ModalContext)

  const location = useLocation()
  const navigate = useNavigate()
  const { provider } = useParams<{ provider: Provider }>()
  const { refetch, refetchCheckLogin } = useUserInfo({ enabled: false })

  const params: string = location.search

  const verifyOAuth = async () => {
    if (typeof provider === 'undefined' || !providers.includes(provider)) {
      navigate('/home')
      return
    }

    try {
      const code = queryString.parse(params)
        ?.code as operations['AuthController_webVerify']['parameters']['query']['code']
      const state = queryString.parse(params)
        ?.state as operations['AuthController_webVerify']['parameters']['query']['state']

      const response = await oAuthVerify({ provider, code, state })
      // console.log({ response })

      // 일단 로그인 성공
      if (response) {
        localStorage.setItem('lastLoginProvider', provider)
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.checkLogin] })
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })
        await refetchCheckLogin()
        await refetch()
      }

      // 로그인 했는데 회원가입이 필요할 때(신규 회원/추가 정보 입력 필요)
      if (response?.isSignup) {
        updateToken('save')
        abrViewTerms()
        ChannelService.track('view_terms')
        navigate('/signUp')
        return
      }

      // 로그인 했는데 회원가입이 필요하지 않을 때(기존 회원/메인화면 이동)
      if (!response?.isSignup) {
        updateToken('save')
        abrUserSignin({ provider })
        ChannelService.track('signin')
        navigate('/')
        return
      }
    } catch (error) {
      console.log({ error })
      if (isAxiosError(error)) {
        if (error.response?.data?.CODE === 'API_001600') {
          openModal({ id: MODALS_ID.ALREADY_WITHDRAWAL_NOTIFY, Component: AlreadyWithdrawalNotifyModal })
        }
      }
    }
  }

  useEffect(() => {
    verifyOAuth()
  }, [])

  return <></>
}
